<template>
  <div class="oa-pay-form">
    <!-- Method & Provider ------------------------------------------------ -->
    <section class="oa-pay-form__section oa-pay-form__section--first">
      <div class="oa-pay-form__section-head">
        <span class="oa-pay-form__eyebrow">Payment Type, Status &amp; Provider</span>
      </div>
      <div class="row g-3">
        <div class="col-md-4">
          <label class="form-label fw-semibold" for="oaPayTypeSelect">
            Payment Type <span class="text-danger">*</span>
          </label>
          <select
            id="oaPayTypeSelect"
            v-model="paymentTypeSelection"
            class="form-select border border-primary"
            @change="onPaymentTypeSelectChange"
          >
            <option v-for="t in rydPaymentTypes" :key="t" :value="t">{{ t }}</option>
            <option value="__CUSTOM__">Custom / Other...</option>
          </select>
          <input
            v-if="paymentTypeSelection === '__CUSTOM__'"
            id="oaPayTypeCustom"
            v-model="model.ryd_payment_type"
            type="text"
            class="form-control border border-primary mt-2"
            placeholder="Enter custom payment type"
          />
        </div>
        <div class="col-md-4">
          <label class="form-label fw-semibold" for="oaPayStatus">
            Status <span class="text-danger">*</span>
          </label>
          <select id="oaPayStatus" v-model="model.status" class="form-select border border-primary">
            <option v-for="opt in paymentStatuses" :key="opt" :value="opt">
              {{ opt }} — {{ statusDescriptions[opt] }}
            </option>
          </select>
        </div>
        <div class="col-md-4">
          <label class="form-label fw-semibold" for="oaPayProviderSelect">Provider</label>
          <select
            id="oaPayProviderSelect"
            v-model="providerSelection"
            class="form-select border border-primary"
            @change="onProviderSelectChange"
          >
            <option v-for="prov in paymentProviders" :key="prov" :value="prov">{{ prov }}</option>
            <option value="__CUSTOM__">Custom / Other...</option>
          </select>
          <input
            v-if="providerSelection === '__CUSTOM__'"
            id="oaPayProviderCustom"
            v-model="model.payment_provider"
            type="text"
            class="form-control border border-primary mt-2"
            placeholder="Enter custom provider name"
          />
        </div>
      </div>
    </section>

    <!-- Amount ---------------------------------------------------------- -->
    <section class="oa-pay-form__section">
      <div class="oa-pay-form__section-head">
        <span class="oa-pay-form__eyebrow">Amount &amp; Date</span>
        <span v-if="expectedAmountTotal != null" class="oa-pay-form__section-help">
          Order total on file: <strong>{{ expectedAmountTotal }} {{ model.currency }}</strong>
        </span>
      </div>
      <div class="row g-3">
        <div class="col-md-5">
          <label class="form-label fw-semibold" for="oaPayAmount">
            Amount <span class="text-danger">*</span>
          </label>
          <input
            id="oaPayAmount"
            v-model.number="model.amount"
            type="number"
            step="0.01"
            class="form-control border border-primary"
          />
        </div>
        <div class="col-md-3">
          <label class="form-label fw-semibold" for="oaPayCurrency">Currency</label>
          <select
            id="oaPayCurrency"
            v-model="model.currency"
            class="form-select border border-primary"
          >
            <option v-for="code in currencies" :key="code" :value="code">{{ code }}</option>
          </select>
        </div>
        <div class="col-md-4">
          <label class="form-label fw-semibold" for="oaPayDate">Date</label>
          <input
            id="oaPayDate"
            v-model="model.date"
            type="date"
            class="form-control border border-primary"
          />
        </div>
      </div>
    </section>

    <!-- Method Details (Conditional) ------------------------------------ -->
    <section class="oa-pay-form__section">
      <!-- Bank Transfer fields -->
      <template v-if="model.ryd_payment_type === 'BANK_TRANSFER'">
        <div class="oa-pay-form__section-head">
          <span class="oa-pay-form__eyebrow">Bank Transfer Details</span>
          <button type="button" class="oa-pay-form__link" @click="applyBankDefaults">
            <i class="fa fa-magic me-1"></i>Use Deutsche Bank defaults
          </button>
        </div>
        <div class="row g-3">
          <div class="col-md-6">
            <label class="form-label fw-semibold" for="oaPayBank">Bank name</label>
            <input
              id="oaPayBank"
              v-model="model.bank_name"
              type="text"
              class="form-control border border-primary"
              placeholder="e.g. Deutsche Bank"
            />
          </div>
          <div class="col-md-6">
            <label class="form-label fw-semibold" for="oaPayIban">Masked IBAN</label>
            <input
              id="oaPayIban"
              v-model="model.masked_iban"
              type="text"
              class="form-control border border-primary font-monospace"
              placeholder="DE11 7207 0001 0032 6058 02"
            />
          </div>
        </div>
      </template>

      <!-- Credit Card fields -->
      <template v-else-if="model.ryd_payment_type === 'CREDIT_CARD'">
        <div class="oa-pay-form__section-head">
          <span class="oa-pay-form__eyebrow">Credit Card Details</span>
        </div>
        <div class="row g-3">
          <div class="col-md-6">
            <label class="form-label fw-semibold" for="oaPayCardBrandSelect">Card Brand</label>
            <select
              id="oaPayCardBrandSelect"
              v-model="cardBrandSelection"
              class="form-select border border-primary"
              @change="onCardBrandSelectChange"
            >
              <option v-for="brand in creditCardBrands" :key="brand" :value="brand">{{ brand }}</option>
              <option value="__CUSTOM__">Custom / Other...</option>
            </select>
            <input
              v-if="cardBrandSelection === '__CUSTOM__'"
              id="oaPayCardBrandCustom"
              v-model="model.credit_card_brand"
              type="text"
              class="form-control border border-primary mt-2"
              placeholder="Enter custom card brand"
            />
          </div>
          <div class="col-md-6">
            <label class="form-label fw-semibold" for="oaPayCardNum">Masked Card Number</label>
            <input
              id="oaPayCardNum"
              v-model="model.masked_credit_card_number"
              type="text"
              class="form-control border border-primary font-monospace"
              placeholder="****-****-****-1111"
            />
          </div>
        </div>
      </template>

      <!-- Shared Payment Reference field -->
      <div class="row g-3 mt-1">
        <div class="col-12">
          <label class="form-label fw-semibold" for="oaPayRef">
            Payment reference
            <span v-if="model.status === 'paid'" class="text-danger">*</span>
          </label>
          <input
            id="oaPayRef"
            v-model="model.payment_reference"
            type="text"
            class="form-control border border-primary font-monospace"
            :placeholder="
              model.status === 'paid' ? 'Required when status is paid' : 'e.g. SEPA-12345 or CC-VISA-987'
            "
          />
          <div v-if="model.status === 'paid'" class="form-text">
            <i class="fa fa-info-circle me-1"></i>
            Required when marking the payment as paid — used for auditing.
          </div>
        </div>
      </div>
    </section>

    <!-- Custom Key-Value Attributes -------------------------------------- -->
    <section class="oa-pay-form__section">
      <div class="oa-pay-form__section-head">
        <span class="oa-pay-form__eyebrow">
          Custom Attributes <span class="oa-pay-form__optional">— key-value pairs</span>
        </span>
        <button type="button" class="oa-pay-form__link" @click="addCustomAttribute">
          <i class="fa fa-plus me-1"></i>Add Attribute
        </button>
      </div>
      <div v-if="customAttributes.length === 0" class="form-text text-muted mb-2">
        No custom attributes added. Use "Add Attribute" to attach extra key-value pairs (e.g. <code>formatted_points</code>, <code>payment_email</code>).
      </div>
      <div v-for="(pair, idx) in customAttributes" :key="idx" class="row g-2 mb-2 align-items-center">
        <div class="col-md-5">
          <input
            v-model="pair.key"
            type="text"
            class="form-control form-control-sm border border-primary font-monospace"
            placeholder="Attribute Key (e.g. payment_email)"
          />
        </div>
        <div class="col-md-6">
          <input
            v-model="pair.value"
            type="text"
            class="form-control form-control-sm border border-primary"
            placeholder="Value"
          />
        </div>
        <div class="col-md-1 text-end">
          <button
            type="button"
            class="btn btn-sm btn-outline-danger"
            title="Remove Attribute"
            @click="removeCustomAttribute(idx)"
          >
            <i class="fa fa-trash"></i>
          </button>
        </div>
      </div>
    </section>

    <!-- Note ------------------------------------------------------------ -->
    <section class="oa-pay-form__section oa-pay-form__section--last">
      <div class="oa-pay-form__section-head">
        <span class="oa-pay-form__eyebrow"
          >Note <span class="oa-pay-form__optional">— optional</span></span
        >
      </div>
      <textarea
        v-model="model.note"
        class="form-control border border-primary"
        rows="3"
        placeholder="Any context worth keeping next to this payment"
      ></textarea>
    </section>

    <!-- Soft warnings --------------------------------------------------- -->
    <div v-if="warnings.length > 0" class="oa-pay-form__warnings">
      <div
        v-for="(w, idx) in warnings"
        :key="idx"
        class="alert alert-warning outline mb-2 oa-pay-form__warning"
      >
        <i class="fa fa-exclamation-triangle me-2"></i>{{ w }}
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, watch } from 'vue';
import {
  PAYMENT_STATUSES,
  PAYMENT_STATUS_DESCRIPTIONS,
  BANK_TRANSFER_DEFAULTS,
  PAYMENT_PROVIDERS,
  RYD_PAYMENT_TYPES,
  CREDIT_CARD_BRANDS,
} from '@/services/ryd-pay-operations/order-amendments/odoo-payment-constants';
import { CURRENCIES } from '@/services/ryd-pay-operations/order-amendments/currency-constants';

const props = defineProps({
  modelValue: { type: Object, default: null },
  expectedAmountTotal: { type: Number, default: null },
});

const emit = defineEmits(['update:modelValue']);

const paymentStatuses = PAYMENT_STATUSES;
const statusDescriptions = PAYMENT_STATUS_DESCRIPTIONS;
const currencies = CURRENCIES;
const paymentProviders = PAYMENT_PROVIDERS;
const rydPaymentTypes = RYD_PAYMENT_TYPES;
const creditCardBrands = CREDIT_CARD_BRANDS;

const KNOWN_FIELDS = new Set([
  'ryd_payment_type',
  'bank_name',
  'masked_iban',
  'credit_card_brand',
  'masked_credit_card_number',
  'date',
  'amount',
  'currency',
  'status',
  'payment_reference',
  'payment_provider',
  'note',
]);

function extractKnownAndCustom(input) {
  const modelPart = {
    ryd_payment_type: input?.ryd_payment_type || 'BANK_TRANSFER',
    bank_name: input?.bank_name ?? null,
    masked_iban: input?.masked_iban || '',
    credit_card_brand: input?.credit_card_brand || '',
    masked_credit_card_number: input?.masked_credit_card_number || '',
    date: input?.date || new Date().toISOString().split('T')[0],
    amount: typeof input?.amount === 'number' ? input.amount : 0,
    currency: input?.currency || 'EUR',
    status: input?.status || 'pending',
    payment_reference: input?.payment_reference || '',
    payment_provider: input?.payment_provider || 'THINXNET',
    note: input?.note || '',
  };

  const customPart = [];
  if (input && typeof input === 'object') {
    for (const [key, val] of Object.entries(input)) {
      if (!KNOWN_FIELDS.has(key)) {
        customPart.push({ key, value: val != null ? String(val) : '' });
      }
    }
  }

  return { modelPart, customPart };
}

const initialParsed = extractKnownAndCustom(props.modelValue);
const model = ref(initialParsed.modelPart);
const customAttributes = ref(initialParsed.customPart);

const paymentTypeSelection = ref(
  rydPaymentTypes.includes(model.value.ryd_payment_type)
    ? model.value.ryd_payment_type
    : '__CUSTOM__'
);

function onPaymentTypeSelectChange() {
  if (paymentTypeSelection.value !== '__CUSTOM__') {
    model.value.ryd_payment_type = paymentTypeSelection.value;
  } else if (rydPaymentTypes.includes(model.value.ryd_payment_type)) {
    model.value.ryd_payment_type = '';
  }
}

const providerSelection = ref(
  paymentProviders.includes(model.value.payment_provider)
    ? model.value.payment_provider
    : '__CUSTOM__'
);

function onProviderSelectChange() {
  if (providerSelection.value !== '__CUSTOM__') {
    model.value.payment_provider = providerSelection.value;
  } else if (paymentProviders.includes(model.value.payment_provider)) {
    model.value.payment_provider = '';
  }
}

const cardBrandSelection = ref(
  creditCardBrands.includes(model.value.credit_card_brand)
    ? model.value.credit_card_brand
    : model.value.credit_card_brand
    ? '__CUSTOM__'
    : creditCardBrands[0]
);

function onCardBrandSelectChange() {
  if (cardBrandSelection.value !== '__CUSTOM__') {
    model.value.credit_card_brand = cardBrandSelection.value;
  } else if (creditCardBrands.includes(model.value.credit_card_brand)) {
    model.value.credit_card_brand = '';
  }
}

watch(
  [model, customAttributes],
  () => {
    const payload = { ...model.value };
    for (const pair of customAttributes.value) {
      if (pair.key && pair.key.trim()) {
        payload[pair.key.trim()] = pair.value;
      }
    }
    emit('update:modelValue', payload);
  },
  { deep: true, immediate: true },
);

function addCustomAttribute() {
  customAttributes.value.push({ key: '', value: '' });
}

function removeCustomAttribute(index) {
  customAttributes.value.splice(index, 1);
}

function applyBankDefaults() {
  const today = new Date().toISOString().split('T')[0];
  const seedAmount =
    props.expectedAmountTotal && props.expectedAmountTotal > 0
      ? props.expectedAmountTotal
      : model.value.amount || 0;
  model.value = {
    ...model.value,
    ...BANK_TRANSFER_DEFAULTS,
    amount: seedAmount,
    date: model.value.date || today,
  };
  paymentTypeSelection.value = 'BANK_TRANSFER';
  providerSelection.value = paymentProviders.includes(BANK_TRANSFER_DEFAULTS.payment_provider)
    ? BANK_TRANSFER_DEFAULTS.payment_provider
    : '__CUSTOM__';
}

const warnings = computed(() => {
  const out = [];
  if (model.value.status === 'paid' && model.value.ryd_payment_type === 'BANK_TRANSFER' && !model.value.masked_iban?.trim()) {
    out.push('Status is "paid" but no IBAN is set — add the masked IBAN for the audit trail.');
  }
  if (
    props.expectedAmountTotal != null &&
    Number.isFinite(model.value.amount) &&
    Math.abs(model.value.amount - props.expectedAmountTotal) > 0.01
  ) {
    out.push(
      `Amount differs from the order total on file (${props.expectedAmountTotal}). Double-check before sending.`,
    );
  }
  return out;
});

const isValid = computed(() => {
  if (!model.value.status) return false;
  if (!model.value.ryd_payment_type?.trim()) return false;
  if (!(Number.isFinite(model.value.amount) && model.value.amount > 0)) return false;
  if (!model.value.currency) return false;
  if (model.value.status === 'paid' && !model.value.payment_reference?.trim()) return false;
  return true;
});

defineExpose({ isValid });
</script>

<style scoped>
.oa-pay-form {
  color: var(--bs-body-color, #212529);
}

/* Section structure ------------------------------------------------------ */
.oa-pay-form__section {
  padding: 1.1rem 0;
  border-top: 1px dashed rgba(15, 23, 42, 0.1);
}

.oa-pay-form__section--first {
  padding-top: 0.25rem;
  border-top: none;
}

.oa-pay-form__section--last {
  padding-bottom: 0.25rem;
}

.oa-pay-form__section-head {
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 0.5rem;
  margin-bottom: 0.75rem;
}

.oa-pay-form__eyebrow {
  text-transform: uppercase;
  letter-spacing: 0.08em;
  font-size: 0.74rem;
  font-weight: 800;
  color: var(--theme-default);
}

.oa-pay-form__optional {
  text-transform: none;
  letter-spacing: 0;
  font-weight: 500;
  color: var(--bs-secondary-color, #6c757d);
  font-size: 0.78rem;
  margin-left: 0.25rem;
}

.oa-pay-form__section-help {
  font-size: 0.82rem;
  color: var(--bs-secondary-color, #6c757d);
}

.oa-pay-form__section-help strong {
  color: var(--bs-body-color, #212529);
  font-weight: 600;
}

.oa-pay-form__link {
  background: transparent;
  border: 0;
  color: var(--theme-default);
  font-size: 0.82rem;
  font-weight: 600;
  padding: 0.2rem 0.5rem;
  border-radius: 0.35rem;
  text-decoration: none;
  transition: background 0.15s ease;
}

.oa-pay-form__link:hover,
.oa-pay-form__link:focus-visible {
  background: rgba(0, 161, 254, 0.1);
  outline: none;
}

/* Labels + inputs -------------------------------------------------------- */
.oa-pay-form :deep(.form-label) {
  color: var(--theme-default);
  font-size: 0.84rem;
  margin-bottom: 0.35rem;
  letter-spacing: 0.01em;
}

.oa-pay-form :deep(.form-control),
.oa-pay-form :deep(.form-select) {
  font-size: 0.95rem;
  padding-top: 0.45rem;
  padding-bottom: 0.45rem;
  color: var(--bs-body-color, #212529);
}

.oa-pay-form :deep(.form-control::placeholder) {
  color: rgba(15, 23, 42, 0.4);
}

.oa-pay-form :deep(.form-text) {
  color: var(--bs-secondary-color, #6c757d);
  font-size: 0.78rem;
  margin-top: 0.3rem;
}

.oa-pay-form :deep(.form-text strong) {
  color: var(--bs-body-color, #212529);
  font-weight: 600;
}

/* Warnings --------------------------------------------------------------- */
.oa-pay-form__warnings {
  margin-top: 1rem;
}

.oa-pay-form__warning {
  font-size: 0.88rem;
  color: var(--bs-body-color, #212529);
}

@media (max-width: 575.98px) {
  .oa-pay-form__section-head {
    flex-direction: column;
    align-items: flex-start;
  }
}
</style>
